import { useState, useEffect } from "react"
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { signIn, adminSessionUtils } from "@/lib/auth-client"

const loginSearchSchema = z.object({
  error: z.string().optional(),
  clearSession: z.string().optional(),
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")

  // Check for error params (from middleware)
  useEffect(() => {
    if (search.error === 'admin_required') {
      setError('Admin access required. Please login with an admin account.')
    }
  }, [search.error])

  // Check if already logged in on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for clearSession flag - if present, skip auth check
        if (search.clearSession === 'true') {
          setIsCheckingAuth(false)
          return
        }

        const isAuth = await adminSessionUtils.isAuthenticated()

        if (isAuth) {
          const redirectTo = search.redirect || '/dashboard'
          navigate({ to: redirectTo, replace: true })
          return
        }
      } catch {
        // User not authenticated, continue to login form
      } finally {
        setIsCheckingAuth(false)
      }
    }

    checkAuth()
  }, [navigate, search])

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p>Checking authentication...</p>
        </div>
      </div>
    )
  }

  async function onSubmit(event: React.SyntheticEvent) {
    event.preventDefault()
    setIsSubmitting(true)
    setError("")

    try {
      // Use Better Auth client for sign in
      const result = await signIn.email({
        email,
        password,
      })

      if (result.error) {
        setError(result.error.message || "Login failed")
        setIsSubmitting(false)
        return
      }

      // Verify admin role after successful login
      const isAdmin = await adminSessionUtils.verifyAdminRole()

      if (!isAdmin) {
        setError("Admin access required. Your account does not have admin privileges.")
        setIsSubmitting(false)
        return
      }

      // Success - redirect to dashboard or specified redirect
      const redirectTo = search.redirect || '/dashboard'
      navigate({ to: redirectTo })
      // Don't call setIsSubmitting(false) - keep form locked during redirect
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "An unexpected error occurred")
      setIsSubmitting(false)
    }
  }

  return (
    <div className="container relative min-h-screen flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
        <div className="absolute inset-0 bg-zinc-900" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          <svg
            className="mr-2 h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
          </svg>
          Genie Admin
        </div>
        <div className="relative z-20 mt-auto">
          <blockquote className="space-y-2">
            <p className="text-lg">
              &ldquo;Genie Admin Dashboard - Manage your creative platform with intelligence.&rdquo;
            </p>
            <footer className="text-sm">Admin Portal</footer>
          </blockquote>
        </div>
      </div>
      <div className="lg:p-8">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Admin Login
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your admin credentials to access the dashboard
            </p>
          </div>
          <Card>
            <form onSubmit={onSubmit}>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl">Sign in</CardTitle>
                <CardDescription>
                  Admin access only - enter your credentials
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@genie.art"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full" type="submit" disabled={isSubmitting}>
                  {isSubmitting && (
                    <svg
                      className="mr-2 h-4 w-4 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                  Sign In
                </Button>
              </CardFooter>
            </form>
          </Card>
          <p className="px-8 text-center text-sm text-muted-foreground">
            Admin access only. No public registration available.
          </p>
        </div>
      </div>
    </div>
  )
}
